import { browserLanguage } from "settings/util/localizationSettings";
import ReportsConstants from "reports/utils/ReportsConstants";
import ReportsMessages from "reports/intl/ReportsMessages";

const { REPORT_TYPES, REPORT_DATE_RANGES } = ReportsConstants;

/**
 * Maps report type keys to their corresponding message definitions
 * for localization lookup
 */
const reportTypeMessageMap = {
  [REPORT_TYPES.ALL_DIMENSIONS_AND_METRICS]:
    ReportsMessages.all_dimensions_and_metrics,
  [REPORT_TYPES.ALL_DIMENSIONS_AND_METRICS_ECOSYSTEM_COST]:
    ReportsMessages.all_dimensions_and_metrics_ecosystem_cost,
  [REPORT_TYPES.APP_TRANSPARENCY]: ReportsMessages.app_transparency,
  [REPORT_TYPES.AUDIENCE_INDEX]: ReportsMessages.audience_index,
  [REPORT_TYPES.AUDIENCE_INDEX_PIXEL]: ReportsMessages.audience_index_pixel,
  [REPORT_TYPES.BRAIN_FEATURE_SUMMARY]: ReportsMessages.brain_feature_summary,
  [REPORT_TYPES.BRAIN_FEATURE_VALUE]: ReportsMessages.brain_feature_value,
  [REPORT_TYPES.BY_HOUR]: ReportsMessages.by_hour,
  [REPORT_TYPES.CONTEXTUAL_INSIGHTS]: ReportsMessages.contextual_insights,
  [REPORT_TYPES.DATA_PIXEL_LOADS]: ReportsMessages.data_pixel_loads,
  [REPORT_TYPES.DAY_PART]: ReportsMessages.day_part,
  [REPORT_TYPES.DEVICE_TECHNOLOGY]: ReportsMessages.device_technology,
  [REPORT_TYPES.EVENT_PIXEL_LOADS]: ReportsMessages.event_pixel_loads,
  [REPORT_TYPES.GEO]: ReportsMessages.geo,
  [REPORT_TYPES.HYPERLOCAL]: ReportsMessages.hyperlocal,
  [REPORT_TYPES.PERFORMANCE]: ReportsMessages.performance,
  [REPORT_TYPES.POSTAL_CODE]: ReportsMessages.postal_code,
  [REPORT_TYPES.REACH_FREQUENCY]: ReportsMessages.reach_frequency,
  [REPORT_TYPES.SITE_TRANSPARENCY]: ReportsMessages.site_transparency,
  [REPORT_TYPES.SITE_TRANSPARENCY_VIEWABILITY]:
    ReportsMessages.site_transparency_viewability,
  [REPORT_TYPES.WATERMARK]: ReportsMessages.watermark,
  [REPORT_TYPES.WIN_LOSS]: ReportsMessages.win_loss,
  [REPORT_TYPES.WIN_LOSS_CREATIVE]: ReportsMessages.win_loss_creative,
};

/**
 * Maps date range keys to their corresponding message definitions
 * for localization lookup
 */
const dateRangeMessageMap = {
  [REPORT_DATE_RANGES.YESTERDAY]: ReportsMessages.yesterday,
  [REPORT_DATE_RANGES.MONTH_TO_DATE]: ReportsMessages.month_to_date,
  [REPORT_DATE_RANGES.CAMPAIGN_TO_DATE]: ReportsMessages.campaign_to_date,
};

/**
 * Regex patterns for parsing dynamic date ranges
 * Captures numeric values (e.g., last_45_days -> 45)
 * Both inclusive and non-inclusive variants map to the same display message
 */
const DATE_RANGE_PATTERNS = [
  { regex: /^last_(\d+)_days(?:_inclusive)?$/, messageKey: "last_x_days" },
  { regex: /^last_(\d+)_hours(?:_inclusive)?$/, messageKey: "last_x_hours" },
];

/**
 * Parses a dynamic date range string and returns the message with count
 *
 * @param {string} dateRange - The date range key (e.g., 'last_110_days')
 * @returns {object|null} Object with message and count, or null if not a dynamic range
 */
const parseDynamicDateRange = (dateRange) => {
  for (const { regex, messageKey } of DATE_RANGE_PATTERNS) {
    const match = dateRange.match(regex);
    if (match) {
      const count = parseInt(match[1], 10);
      const message = ReportsMessages[messageKey];
      if (message) {
        return { message, count };
      }
    }
  }
  return null;
};

/**
 * Gets the localized message definition for a given report type
 *
 * @param {string} reportType - The report type key (e.g., 'all_dimensions_and_metrics')
 * @returns {object|null} The message definition object with id and defaultMessage, or null if not found
 *
 * @example
 * const messageDefinition = getReportTypeMessage('all_dimensions_and_metrics');
 * // Returns: { id: 'reports.myReports.reportType.allDimensionsAndMetrics', defaultMessage: 'All Dimensions and Metrics' }
 *
 * // Use with react-intl's formatMessage:
 * const localizedName = formatMessage(getReportTypeMessage(reportType));
 */
export const getReportTypeMessage = (reportType) => {
  const msg = reportTypeMessageMap[reportType];
  if (msg) return msg;
  throw new Error("Invalid report type");
};

/**
 * Gets the localized message definition for a given date range
 *
 * @param {string} dateRange - The date range key (e.g., 'last_45_days', 'last_110_days')
 * @returns {object} Object containing message definition and optional values for parameterized messages
 *   - message: The message definition object with id and defaultMessage, or null if invalid
 *   - values: Object with parameter values (e.g., { count: 110 }) for parameterized messages, or empty object
 *   - fallback: The original dateRange string (only present when message is null)
 *
 * @example
 * // Static date range
 * const { message, values } = getDateRangeMessage('yesterday');
 * const localizedName = formatMessage(message, values);
 * // Returns: "Yesterday"
 *
 * @example
 * // Dynamic date range
 * const { message, values } = getDateRangeMessage('last_110_days');
 * const localizedName = formatMessage(message, values);
 * // Returns: "Last 110 Days"
 *
 * @example
 * // Unknown date range (graceful fallback)
 * const { message, fallback } = getDateRangeMessage('unknown_range');
 * const displayText = message ? formatMessage(message, values) : fallback;
 * // Returns: "unknown_range"
 */
export const getDateRangeMessage = (dateRange) => {
  // Check static mappings first
  const staticMsg = dateRangeMessageMap[dateRange];
  if (staticMsg) {
    return { message: staticMsg, values: {} };
  }

  // Try to parse as a dynamic date range (e.g., last_45_days)
  const dynamicResult = parseDynamicDateRange(dateRange);
  if (dynamicResult) {
    return {
      message: dynamicResult.message,
      values: { count: dynamicResult.count },
    };
  }

  return { message: null, values: {}, fallback: dateRange };
};

/**
 * Formats a date string into a localized date format
 *
 * @param {string} serverDate - ISO date string from the server
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in the user's locale
 *
 * @example
 * const formatted = getLocalizedReportDate('2024-01-15T10:30:00Z');
 * // Returns: "Jan 15, 2024" (in user's locale)
 */
export const getLocalizedReportDate = (
  serverDate,
  options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }
) => {
  if (!serverDate) return null;

  try {
    const date = new Date(Date.parse(serverDate));
    return new Intl.DateTimeFormat(browserLanguage, options).format(date);
  } catch {
    return serverDate;
  }
};

/**
 * Formats a date string into a localized date-time format
 *
 * @param {string} serverDate - ISO date string from the server
 * @returns {string} Formatted date-time string in the user's locale
 *
 * @example
 * const formatted = getLocalizedReportDateTime('2024-01-15T10:30:00Z');
 * // Returns: "01/15/24, 10:30:00 AM" (in user's locale)
 */
export const getLocalizedReportDateTime = (serverDate) => {
  const DATE_FORMAT = "2-digit";
  const formatOpts = {
    year: DATE_FORMAT,
    month: DATE_FORMAT,
    day: DATE_FORMAT,
    hour: DATE_FORMAT,
    minute: DATE_FORMAT,
    second: DATE_FORMAT,
  };

  return getLocalizedReportDate(serverDate, formatOpts);
};

/**
 * Triggers a browser download for CSV data
 *
 * @param {string} csvData - The CSV data as a string
 * @param {string} filename - The desired filename for the download (e.g., 'report.csv')
 *
 * @example
 * triggerCSVDownload('header1,header2\nvalue1,value2', 'my-report.csv');
 * // Triggers browser download of 'my-report.csv'
 */
export const triggerCSVDownload = (csvData, filename) => {
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
